import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { allIntervieweesQueryOptions, mapVillagesQueryOptions } from "../../services/queries/publicQueryOptions";
import { LISTING_PAGE_SIZE } from "../../services/paginationConfig";
import { buildIntervieweeMiniCardMeta, buildVillageNameLookup } from "../../services/intervieweeListing";
import { ListingPaginationControls } from "../../components/common/ListingPaginationControls";

type IntervieweesPageProps = {
  onSelectInterviewee: (intervieweeRef: string, villageId: string) => void;
};

export function IntervieweesPage({ onSelectInterviewee }: IntervieweesPageProps) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const normalizedQuery = query.trim();
  const intervieweesQuery = useQuery(allIntervieweesQueryOptions(page, LISTING_PAGE_SIZE, normalizedQuery));
  const mapVillagesQuery = useQuery(mapVillagesQueryOptions());
  const interviewees = intervieweesQuery.data?.items ?? [];
  const totalFiltered = intervieweesQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / LISTING_PAGE_SIZE));
  const villageNameLookup = useMemo(
    () => buildVillageNameLookup(mapVillagesQuery.data?.items ?? []),
    [mapVillagesQuery.data?.items],
  );

  useEffect(() => {
    if (!intervieweesQuery.data) {
      return;
    }

    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [intervieweesQuery.data, page, totalPages]);

  const pageItems = interviewees;

  if (intervieweesQuery.isLoading && !intervieweesQuery.data) {
    return (
      <section className="listing-page">
        <div className="listing-page__inner">
          <h1 className="listing-page__heading fade-in">Survivors</h1>
          <p className="listing-empty">Loading survivors...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="listing-page">
      <div className="listing-page__inner">
        <h1 className="listing-page__heading fade-in">Survivors</h1>

        <div className="listing-controls fade-in fade-in--delay-1">
          <input
            type="text"
            className="listing-search"
            placeholder="Search survivors..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
          />
          <span className="listing-count">{totalFiltered} survivors</span>
          <div className="listing-controls__right">
            <ListingPaginationControls
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              className="listing-pagination--inline"
            />
          </div>
        </div>

        <div className="listing-grid listing-grid--interviewees fade-in fade-in--delay-2">
          {pageItems.map((item) => {
            const cardMeta = buildIntervieweeMiniCardMeta(item, villageNameLookup);
            return (
              <button
                key={item.id}
                type="button"
                className="mini-card interactive-card"
                onClick={() => onSelectInterviewee(item.id, item.villageId)}
              >
                <div className="mini-card-thumb">
                  <img src="/profile.png" alt="" loading="lazy" />
                </div>
                <div className="mini-card-body">
                  <p className="card-row" title={cardMeta.aliasValue}>{cardMeta.aliasLine}</p>
                  <p className="card-row">{cardMeta.displacedLine}</p>
                  <p className="card-row" title={cardMeta.villageValue}>{cardMeta.villageLine}</p>
                </div>
              </button>
            );
          })}
          {pageItems.length === 0 && !intervieweesQuery.isLoading && (
            <p className="listing-empty">No interviewees match your search.</p>
          )}
        </div>
      </div>
    </section>
  );
}
